import { Injectable, OnDestroy } from '@angular/core';

import { Subscription } from 'rxjs';

import { ComparisonHandlerService } from 'app/modules/dashboard/services/comparison-handler-service';
import { ReportService } from 'app/providers/storage-api/report.service';
import { EnableClassTelemetry } from 'app/services/otel.service';

import { TimezoneService } from 'app/services/timezone.service';
import { StorageAccountService } from '../../../providers/storage-api/blender-account.service';
import { ProjectService } from '../../../providers/storage-api/project.service';
import { WidgetChartOptions } from '../components/widget-chart/widget-chart.component';
import { CrosstabWidgetOptions } from '../components/widget-crosstab/widget-crosstab.component';
import { FilterService } from '../../collection/services/filter.service';
import * as ChartReports from './chart-report';

import { AbstractChartReport } from './chart-report/chart-report-abstract/chart-report-abstract';

@EnableClassTelemetry()
@Injectable()
export class ChartReportService implements OnDestroy {
  protected fieldTypeMap: Map<string, string> = new Map();
  public fieldTypeMapPromise: Promise<Map<string, string>>;

  private groupFieldSub$: Subscription;

  constructor(
    protected reportService: ReportService,
    protected filterService: FilterService,
    protected projectService: ProjectService,
    protected accountService: StorageAccountService,
    protected comparisonService: ComparisonHandlerService,
    protected timezoneService: TimezoneService
  ) {
    this.fieldTypeMapPromise = new Promise((resolve) => {
      this.groupFieldSub$ = this.accountService.groupFieldsSub.subscribe((accountMetadata) => {
        accountMetadata.metadata.forEach((f) => this.fieldTypeMap.set(f.fieldName, f.fieldType));
        resolve(this.fieldTypeMap);
      });
    });
  }

  ngOnDestroy() {
    if (this.groupFieldSub$) {
      this.groupFieldSub$.unsubscribe();
    }
  }

  public configureReport(payload: WidgetChartOptions): AbstractChartReport {
    switch (payload.type) {
      case 'documentCount':
        if (payload.analyticItem === 'documents') {
          if (payload.aggregator === 'time' || payload.aggregator === 'percentage_time') {
            return new ChartReports.DocumentsOverTimeChartReport(
              payload,
              this.reportService,
              this.filterService,
              this.comparisonService,
              this.timezoneService
            );
          }
          return new ChartReports.DocumentsChartReport(
            payload,
            this.reportService,
            this.filterService,
            this.comparisonService,
            this.timezoneService
          );
        } else {
          if (
            (payload.aggregator === 'time' && payload.analyticItem !== 'documents') ||
            payload.aggregator === 'percentage_time'
          ) {
            if (payload.customFieldSelected || payload.standardFieldSelected) {
              return new ChartReports.DocumentTopMetadataOverTimeChartReport(
                payload,
                this.reportService,
                this.filterService,
                this.comparisonService,
                this.timezoneService,
                this.fieldTypeMap.get(payload.analyticItem)
              );
            }
            if (payload.analyticItem === 'taxonomy') {
              return new ChartReports.TaxonomyTopFeaturesOverTimeChatReport(
                payload,
                this.reportService,
                this.filterService,
                this.comparisonService,
                this.timezoneService,
                this.fieldTypeMap.get(payload.analyticItem)
              );
            }

            return new ChartReports.DocumentTopFeaturesOverTimeChartReport(
              payload,
              this.reportService,
              this.filterService,
              this.comparisonService,
              this.timezoneService
            );
          } else {
            if (payload.customFieldSelected || payload.standardFieldSelected) {
              return new ChartReports.MetadataTopFeaturesChartReport(
                payload,
                this.reportService,
                this.filterService,
                this.comparisonService,
                this.timezoneService,
                this.fieldTypeMap.get(payload.analyticItem)
              );
            }
            if (payload.analyticItem === 'taxonomy') {
              return new ChartReports.TaxonomyTopFeaturesChartReport(
                payload,
                this.reportService,
                this.filterService,
                this.comparisonService,
                this.timezoneService,
                this.fieldTypeMap.get(payload.analyticItem)
              );
            }
            return new ChartReports.DocumentTopFeaturesChartReport(
              payload,
              this.reportService,
              this.filterService,
              this.comparisonService,
              this.timezoneService
            );
          }
        }

      case 'numericMetadata':
      case 'journey':
        return new ChartReports.DocumentNumericMetadataOverTimeChartReport(
          payload,
          this.reportService,
          this.filterService,
          this.comparisonService,
          this.timezoneService
        );

      case 'sentimentPolarity':
        if (payload.analyticItem === 'documents') {
          if (payload.aggregator === 'time' || payload.aggregator === 'percentage_time') {
            return new ChartReports.DocumentsSentimentPolarityOverTimeChartReport(
              payload,
              this.reportService,
              this.filterService,
              this.comparisonService,
              this.timezoneService
            );
          } else {
            return new ChartReports.DocumentsSentimentPolarityChartReport(
              payload,
              this.reportService,
              this.filterService,
              this.comparisonService,
              this.timezoneService
            );
          }
        } else {
          if (payload.customFieldSelected || payload.standardFieldSelected) {
            return new ChartReports.DocumentTopMetadataSentimentPolarityChartReport(
              payload,
              this.reportService,
              this.filterService,
              this.comparisonService,
              this.timezoneService,
              this.fieldTypeMap.get(payload.analyticItem)
            );
          }
          if (payload.analyticItem === 'taxonomy') {
            return new ChartReports.TaxonomySentimentPolarityPolarityChartReport(
              payload,
              this.reportService,
              this.filterService,
              this.comparisonService,
              this.timezoneService
            );
          }
          return new ChartReports.DocumentTopFeaturesSentimentPolarityChartReport(
            payload,
            this.reportService,
            this.filterService,
            this.comparisonService,
            this.timezoneService
          );
        }

      case 'powerScore':
        return new ChartReports.DocumentsPowerScoreChartReport(
          payload,
          this.reportService,
          this.filterService,
          this.comparisonService,
          this.timezoneService
        );

      case 'powerScoreOverTime':
        if (payload.analyticItem === 'documents') {
          return new ChartReports.DocumentsPowerScoreOverTimeReport(
            payload,
            this.reportService,
            this.filterService,
            this.comparisonService,
            this.timezoneService
          );
        }

        return new ChartReports.FeaturePowerScoreOverTimeChartReport(
          payload,
          this.reportService,
          this.filterService,
          this.comparisonService,
          this.timezoneService
        );

      case 'sentimentScore':
        if (payload.customFieldSelected || payload.standardFieldSelected) {
          if (['SUM', 'AVG'].indexOf(payload.aggregator) >= 0) {
            return new ChartReports.MetadataSentimentScoreOverTimeChartReport(
              payload,
              this.reportService,
              this.filterService,
              this.comparisonService,
              this.timezoneService,
              this.fieldTypeMap.get(payload.analyticItem)
            );
          } else {
            return new ChartReports.MetadataSentimentScoreChartReport(
              payload,
              this.reportService,
              this.filterService,
              this.comparisonService,
              this.timezoneService,
              this.fieldTypeMap.get(payload.analyticItem)
            );
          }
        }
        if (payload.analyticItem === 'taxonomy') {
          if (['SUM', 'AVG'].indexOf(payload.aggregator) >= 0) {
            return new ChartReports.TaxonomySentimentScoreOverTimeChartReport(
              payload,
              this.reportService,
              this.filterService,
              this.comparisonService,
              this.timezoneService
            );
          } else {
            return new ChartReports.TaxonomySentimentScoreChartReport(
              payload,
              this.reportService,
              this.filterService,
              this.comparisonService,
              this.timezoneService,
              this.fieldTypeMap.get(payload.analyticItem)
            );
          }
        }
        if (payload.analyticItem === 'documents') {
          return new ChartReports.DocumentsSentimentScoreOverTimeReport(
            payload,
            this.reportService,
            this.filterService,
            this.comparisonService,
            this.timezoneService
          );
        } else {
          if (['SUM', 'AVG'].indexOf(payload.aggregator) >= 0) {
            return new ChartReports.FeatureSentimentScoreOverTimeChartReport(
              payload,
              this.reportService,
              this.filterService,
              this.comparisonService,
              this.timezoneService
            );
          } else {
            return new ChartReports.FeatureSentimentScoreChartReport(
              payload,
              this.reportService,
              this.filterService,
              this.comparisonService,
              this.timezoneService
            );
          }
        }

      case 'sentimentVolume':
        if (payload.customFieldSelected || payload.standardFieldSelected) {
          return new ChartReports.MetadataFeatureVolumeVersusSentimentChartReport(
            payload,
            this.reportService,
            this.filterService,
            this.comparisonService,
            this.timezoneService,
            this.fieldTypeMap.get(payload.analyticItem)
          );
        }
        if (payload.analyticItem === 'taxonomy') {
          return new ChartReports.TaxonomyVolumeVersusSentimentChartReport(
            payload,
            this.reportService,
            this.filterService,
            this.comparisonService,
            this.timezoneService
          );
        }
        return new ChartReports.FeatureVolumeVersusSentimentChartReport(
          payload,
          this.reportService,
          this.filterService,
          this.comparisonService,
          this.timezoneService
        );

      case 'taxonomyMap':
        return new ChartReports.TaxonomyMapChartReport(
          payload,
          this.reportService,
          this.filterService,
          this.comparisonService,
          this.timezoneService,
          this.projectService
        );

      case 'topMovers':
        return new ChartReports.TopMoversChartReport(
          payload,
          this.reportService,
          this.filterService,
          this.comparisonService,
          this.timezoneService
        );

      case 'wordcloud':
        if (payload.customFieldSelected || payload.standardFieldSelected) {
          return new ChartReports.MetadataWordcloudChartReport(
            payload,
            this.reportService,
            this.filterService,
            this.comparisonService,
            this.timezoneService,
            this.projectService,
            this.fieldTypeMap.get(payload.analyticItem)
          );
        }
        if (payload.analyticItem === 'taxonomy') {
          return new ChartReports.TaxonomyWordCloudChartReport(
            payload,
            this.reportService,
            this.filterService,
            this.comparisonService,
            this.timezoneService,
            this.projectService,
            this.fieldTypeMap.get(payload.analyticItem)
          );
        }
        return new ChartReports.WordcloudChartReport(
          payload,
          this.reportService,
          this.filterService,
          this.comparisonService,
          this.timezoneService,
          this.projectService,
          this.fieldTypeMap.get(payload.analyticItem)
        );

      case 'crosstab':
        const rowType = this.fieldTypeMap.get(payload.analyticItem);
        const columnType = this.fieldTypeMap.get(
          (payload as CrosstabWidgetOptions).columnAnalyticItem
        );
        let metadataType;

        if (rowType || columnType) {
          metadataType = `${rowType || ''}|${columnType || ''}`;
        }

        return new ChartReports.CrosstabChartReport(
          payload,
          this.reportService,
          this.filterService,
          this.comparisonService,
          this.timezoneService,
          metadataType
        );

      case 'featureScoring':
        if (payload.isDrilledDown) {
          return new ChartReports.MetadataWordcloudChartReport(
            payload,
            this.reportService,
            this.filterService,
            this.comparisonService,
            this.timezoneService,
            this.projectService,
            this.fieldTypeMap.get(payload.analyticItem)
          );
        }

        return new ChartReports.TopicScoringReport(
          payload,
          this.reportService,
          this.filterService,
          this.comparisonService,
          this.timezoneService
        );

      case 'scatterPlot':
        return new ChartReports.ScatterPlotChartReport(
          payload,
          this.reportService,
          this.filterService,
          this.comparisonService,
          this.timezoneService
        );

      case 'impact':
        return new ChartReports.ImpactReport(
          payload,
          this.reportService,
          this.filterService,
          this.comparisonService,
          this.timezoneService,
          this.fieldTypeMap.get(payload.analyticItem)
        );

      case 'bigNumber':
        if (payload.analyticItem === 'documents') {
          return new ChartReports.BigNumberDocumentsReport(
            payload,
            this.reportService,
            this.filterService,
            this.comparisonService,
            this.timezoneService
          );
        } else {
          return new ChartReports.BigNumberMetadataReport(
            payload,
            this.reportService,
            this.filterService,
            this.comparisonService,
            this.timezoneService
          );
        }

      case 'breakdown':
        return new ChartReports.BreakdownChartReport(
          payload,
          this.reportService,
          this.filterService,
          this.comparisonService,
          this.timezoneService
        );

      case 'donut':
        return new ChartReports.DonutChartReport(
          payload,
          this.reportService,
          this.filterService,
          this.comparisonService,
          this.timezoneService
        );

      case 'gauge':
        return new ChartReports.GaugeChartReport(
          payload,
          this.reportService,
          this.filterService,
          this.comparisonService,
          this.timezoneService
        );
      case 'focusAreas':
        return new ChartReports.ImpactReport(
          payload,
          this.reportService,
          this.filterService,
          this.comparisonService,
          this.timezoneService,
          this.fieldTypeMap.get(payload.analyticItem)
        );
      default:
        break;
    }
  }
}
